'use client';

import { useEffect, useRef } from 'react';

import { useSubscription } from '@apollo/client/react';

import { UT_REMOTE_VOICE_COMMAND } from '@/survey-graphql';

import { resolveRemoteVoiceAction } from './shared-survey-mobile-ut-remote-voice.helpers';
import { VoiceCommandEvent } from './shared-survey-project-response-mode.types';

export interface UtRemoteVoiceTurn {
  transcript: string;
  isFinal: boolean;
  command: VoiceCommandEvent | null;
}

interface Props {
  uniqueId: string | null;
  enabled?: boolean;
  isBlocked?: boolean;
  onTurn: (turn: UtRemoteVoiceTurn) => void;
}

// test-exception: Apollo subscription binding only — the turn resolution it delegates to is covered by shared-survey-mobile-ut-remote-voice.helpers.spec.ts.
export function useUtRemoteVoiceCommands({
  uniqueId,
  enabled = true,
  isBlocked = false,
  onTurn,
}: Props): void {
  // Read latest pause/over state inside the long-lived subscription callback without resubscribing (resubscribe would drop the WebSocket on every pause).
  const isBlockedRef = useRef(isBlocked);

  useSubscription(UT_REMOTE_VOICE_COMMAND, {
    variables: { uniqueId: uniqueId ?? '' },
    skip: !enabled || !uniqueId,
    onData: ({ data: { data } }) => {
      // While paused or over, drop the whole relayed turn so ambient speech never appends to the live transcript.
      const action = resolveRemoteVoiceAction(
        data?.utRemoteVoiceCommand,
        isBlockedRef.current
      );
      if (!action) return;

      onTurn(action);
    },
  });

  useEffect(() => {
    isBlockedRef.current = isBlocked;
  }, [isBlocked]);
}
